import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as ReactBootstrap from 'react-bootstrap'
import { Field, reduxForm, InjectedFormProps, change } from 'redux-form'
import { renderers, validations, FormButtons } from '../../components'
import { getLists } from '../../state/lists'
import * as rest from '../../services'
import { showErrorToast, showSuccessToast } from '../../state/toasts'
import {StatusCommunication} from './Communication/StatusCommunication'
import DeliveryOTP from './DeliveryOTP'

interface Ticket {
  url: string;
  status: string;
  is_delivery_otp_verified: boolean;
}

interface TicketStatusOption {
  url: string;
  value: string;
}

interface TicketStatusFormProps {
  ticket_status: TicketStatusOption[] | null;
  ticket: Ticket;
}

interface StatusUpdateProps {
  ticket: Ticket;
  onTicketUpdate: (updatedTicket: Ticket) => void;
}

/**
 * TicketStatusForm Component
 *
 * Renders the form for updating the ticket status.
 *
 * @param {object} props - The component props
 * @param {Function} props.handleSubmit - Redux form submit handler
 * @param {boolean} props.pristine - Form pristine state
 * @param {Function} props.reset - Form reset handler
 * @param {boolean} props.submitting - Form submission state
 * @param {Array} props.ticket_status - List of ticket statuses
 * @param {object} props.ticket - Ticket details
 *
 * @returns {JSX.Element}
 */
const TicketStatusForm: React.FC<InjectedFormProps & TicketStatusFormProps> = ({
  handleSubmit,
  pristine,
  reset,
  submitting,
  ticket_status,
  ticket,
}) => (
  <form onSubmit={handleSubmit}>
    <ReactBootstrap.Row>
      <ReactBootstrap.Col xs={12}>
        <Field
          component={renderers.renderSelectField}
          as="select"
          name="status"
          label="Status"
          validate={[validations.required]}
        >
          {ticket_status?.map((sp) => (
            <option key={sp.url} value={sp.value}>
              {sp.value}
            </option>
          ))}
        </Field>
        <ReactBootstrap.Alert variant="warning">
          Update Device Pickup Time to change Status to Delivered.
        </ReactBootstrap.Alert>
        <ReactBootstrap.Alert variant="info">
          <Field
            name="delivering_now"
            id="delivering_now"
            component="input"
            type="checkbox"
            disabled={!ticket.is_delivery_otp_verified}
          />
          &nbsp;&nbsp;Delivering Now? Verify Delivery OTP to enable this.
        </ReactBootstrap.Alert>
      </ReactBootstrap.Col>
    </ReactBootstrap.Row>
    <FormButtons submitting={submitting} pristine={pristine} reset={reset} />
  </form>
)

const TicketStatusReduxForm = reduxForm<TicketStatusFormProps>({
  form: 'ticket-status-form',
})(TicketStatusForm)

/**
 * StatusUpdate Component
 *
 * Handles the ticket status update and OTP verification UI.
 *
 * @param {object} props - The component props
 * @param {object} props.ticket - Ticket details
 * @param {Function} props.onTicketUpdate - Callback after ticket update
 *
 * @returns {JSX.Element}
 */
export const StatusUpdate: React.FC<StatusUpdateProps> = ({ ticket, onTicketUpdate }) => {
  const dispatch = useDispatch()
  const ticket_status = useSelector((state: any) => state.lists.ticket_status)

  useEffect(() => {
    if (!ticket_status) {
      dispatch(getLists('TICKET_STATUS'))
    }
    dispatch(change('ticket-status-form', 'status', ticket.status))
  }, [dispatch, ticket_status, ticket])

  const updateTicketStatus = async (status: string) => {
    const service = new rest.TicketService(ticket.url)
    try {
      const response = await service.change_status(status)
      dispatch(showSuccessToast('Ticket Status update', `Ticket status has been updated to ${response.status}.`))
      onTicketUpdate(response )
    } catch (error) {
      dispatch(showErrorToast('Error On Status update', JSON.stringify(error.response?.data)))
    }
  }

  return (
    <>
      <div className="pe-3" style={{ textAlign: 'right', display: 'flex', flexDirection: 'row-reverse', gap: '12px' }}>
        <span style={{ display: 'inline-block' }}>
          <StatusCommunication ticket={ticket} />
        </span>
        <span className="mr-3" style={{ display: 'inline-block' }}>
          <DeliveryOTP ticket={ticket} onOTPVerificationSuccess={onTicketUpdate} />
        </span>
      </div>
      <TicketStatusReduxForm ticket={ticket} ticket_status={ticket_status} onSubmit={updateTicketStatus} />
    </>
  )
}
